import { forwardRef, useId, useState } from 'react';
import clsx from 'clsx';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

/**
 * Password input with a show/hide toggle.
 *
 * NOTE: belongs in components/ui alongside Input (ideally as an Input variant),
 * but that directory is owned elsewhere in this change.
 */
const PasswordField = forwardRef(({ label, error, hint, className, id, ...props }, ref) => {
  const [visible, setVisible] = useState(false);
  const reactId = useId();
  const inputId = id || `password-${reactId}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const describedBy = clsx(hint && !error && hintId, error && errorId) || undefined;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          type={visible ? 'text' : 'password'}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          className={clsx(
            'input-field pr-12',
            error && 'border-danger-500 focus:ring-danger-100 focus:border-danger-500'
          )}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          className="absolute right-0 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 focus-ring"
        >
          {visible ? <EyeSlashIcon className="h-5 w-5" aria-hidden="true" /> : <EyeIcon className="h-5 w-5" aria-hidden="true" />}
        </button>
      </div>
      {hint && !error && <p id={hintId} className="mt-1.5 text-xs text-slate-500">{hint}</p>}
      {error && (
        <div role="alert">
          <p id={errorId} className="mt-1.5 text-sm text-danger-700">{error}</p>
        </div>
      )}
    </div>
  );
});

PasswordField.displayName = 'PasswordField';
export default PasswordField;
